import produce from 'immer';
import { Action, Reducer } from 'redux';
import { changeLangSurvey, isRadio } from '../common/helperFunctions';
import { SurveyModel } from '../models/survey.model';
import { AnswerIdValueModel, MatrixAnswerModel, McqAnswerModel, SurveySubmissionModel } from '../models/SurveySubmission.model';
import { AppThunkAction } from './';
import * as fromConstants from '../common/constants';
import { MatrixRowAndColumnText_Seperator } from '../common/constants';
import authHeader from '../services/auth-header';
import AuthService from '../services/auth.service';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface DataState {
    isLoading: boolean,
    isSubmitted: boolean,
    errorInSubmit: boolean,
    language: string,
    step: number,
    surveyCode: string,
    survey: SurveyModel,
}

export const FETCH_SURVEY = '[SURVEY] FETCH_SURVEY';
export const RECEIVE_SURVEY = '[SURVEY] RECEIVE_SURVEY';
export const SUBMIT_SURVEY = '[SURVEY] SUBMIT_SURVEY';
export const MOVE_NEXT_STEP = '[SURVEY] MOVE_NEXT_STEP';
export const SET_VALIDATION_STATUS = '[SURVEY] SET_VALIDATION_STATUS';
export const SET_VALIDATION_STATUS_AND_VALUE = '[SURVEY] SET_VALIDATION_STATUS_AND_VALUE';
export const SET_SUBMISSION_FLAG = '[SURVEY] SET_SUBMISSION_FLAG';
export const SET_LANGUAGE = '[SURVEY] SET_LANGUAGE';

interface FetchSurveyAction {
    type: string;
    surveyCode: string;
    survey: SurveyModel;
}

interface ReceiveSurveyAction {
    type: string;
    surveyCode: string;
    survey: SurveyModel;
}

interface SubmitSurveyAction {
    type: string;
    errorInSubmit: boolean
}

interface MoveNextStepSurveyAction {
    type: string;
    step?: number;
}

interface SetValidationStatusAction {
    type: string;
    questionId: number;
    validationStatus: boolean;
}

interface SetValidationStatusAndValueAction {
    type: string;
    answerValue: AnswerValue;
}

interface AnswerValue {
    questionId: number;
    optionId: number;
    columnId: number;
    rowId: number;

    text: string;
    isChecked: boolean;
    isOtherTextBoxChanged: boolean;
    isValid: boolean;
    isSubmitted: boolean;
}

interface SetSubmissionFlagAction {
    type: string;
}

interface SetLanguageSurveyAction {
    type: string;
    language: string;
    moveNext: boolean;
}


type KnownAction = 
    FetchSurveyAction
    | ReceiveSurveyAction
    | SubmitSurveyAction
    | MoveNextStepSurveyAction
    | SetValidationStatusAction
    | SetValidationStatusAndValueAction
    | SetSubmissionFlagAction
    | SetLanguageSurveyAction;


    export const actionCreators = {
    MoveNextStep: (step?: number): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.surveyStore && appState.surveyStore.surveyCode) {
            dispatch({ type: MOVE_NEXT_STEP, step: step });
        }
    },
    RequestSurvey: (surveyCode: string): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.surveyStore && surveyCode !== appState.surveyStore.surveyCode) {
            const baseurl = window.location.origin;

            const user = AuthService.getCurrentUser();

            // const url = (baseurl.indexOf("uat") > -1 || baseurl.indexOf("admin") > -1 || baseurl.indexOf("localhost") > -1)  ? 'admin/SearchDraftByLink' : 'Survey/SearchByLink';
            let  url = 'Survey/SearchByLink';
            if (user.accessToken)
                url = 'admin/SearchDraftByLink' ;

            fetch(`/${url}/${surveyCode}`, authHeader("GET", '', ''))
                .then(response => response.json() as Promise<SurveyModel>)
                .then(data => {
                    dispatch({ type: RECEIVE_SURVEY, surveyCode: surveyCode, survey: data });
                })
                .catch(error => {
                    dispatch({ type: MOVE_NEXT_STEP, step: -1 });
                });

            dispatch({ type: FETCH_SURVEY, surveyCode: surveyCode, survey: new SurveyModel() });
        //} else if (appState && appState.survey && appState.survey.surveyCode) {
        //    dispatch({ type: MOVE_NEXT_STEP, step: -1 });
        } else if (surveyCode === '') {
            dispatch({ type: MOVE_NEXT_STEP, step: -1 });
        }
    },
    SubmitSurvey: (isValid: boolean): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {
        const appState = getState();
        if (isValid && appState && appState.surveyStore && appState.surveyStore.surveyCode !== null) {
            const rqst = new SurveySubmissionModel();
            rqst.code = appState.surveyStore.surveyCode;

            rqst.Answers = appState.surveyStore.survey.questions.map(a => 
                                new AnswerIdValueModel(
                                        a.questionId, a.value,
                                        (a.mcqAnswers || []).map(x => new  McqAnswerModel(x.mcqOptionId, x.answerEn, x.answerFr) ),
                                        (a.matrixAnswers || []).map(x => new  MatrixAnswerModel(x.matrixRowId, x.matrixColumnId, x.answerEn, x.answerFr) )
                                    )
                            );

            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(rqst)
            };

            fetch('/Survey/Submit', requestOptions)
                .then(response => {
                    if (response.ok)
                        dispatch({ type: MOVE_NEXT_STEP, step: undefined });
                    else
                        dispatch({ type: SUBMIT_SURVEY, errorInSubmit: true });
                })
                .catch(error => {
                    dispatch({ type: SUBMIT_SURVEY, errorInSubmit: true });
                });

        }

        dispatch({ type: SET_SUBMISSION_FLAG, errorInSubmit: false });
    },
    SetValidationStatus: ( questionId: number, validationStatus: boolean ): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.surveyStore && appState.surveyStore.surveyCode) {
            dispatch({ type: SET_VALIDATION_STATUS, questionId, validationStatus });
        }
    },
    SetValidationStatusAndValue: ( answerValue: AnswerValue ): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.surveyStore && appState.surveyStore.surveyCode) {
            dispatch({ type: SET_VALIDATION_STATUS_AND_VALUE, answerValue: answerValue });
        }
    },
    SetLanguage: (language: string, moveNext: boolean ): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.surveyStore && appState.surveyStore.surveyCode) {
            dispatch({ type: SET_LANGUAGE, language: language, moveNext: moveNext });
        }
    }
};

const unloadedState: DataState = {
    isLoading: false,
    isSubmitted: false,
    errorInSubmit: false,
    language: 'en',
    surveyCode: '',
    step: 0,
    survey: new SurveyModel()
};

export const reducer: Reducer<DataState> = (state: DataState = unloadedState, incomingAction: Action): DataState => {
    if (state === undefined) {
        return unloadedState;
    }


    if (incomingAction.type === FETCH_SURVEY) {
        const action = incomingAction as FetchSurveyAction;
        return {...state,
            isLoading: true,
            surveyCode: action.surveyCode,
            step: 0,
            survey: new SurveyModel()
        };
    } else if (incomingAction.type === RECEIVE_SURVEY) {
        const action = incomingAction as ReceiveSurveyAction;
        if (action.surveyCode === state.surveyCode) {
            return {...state,
                isLoading: false,
                surveyCode: action.surveyCode,
                step: 1,
                survey: changeLangSurvey(action.survey, state.language)
            };
        }
    } else if (incomingAction.type === SUBMIT_SURVEY) {
        const action = incomingAction as SubmitSurveyAction;

        return  { ...state, isSubmitted: true, errorInSubmit: action.errorInSubmit };
    } else if (incomingAction.type === MOVE_NEXT_STEP) {
        const action = incomingAction as MoveNextStepSurveyAction;
        let stepLevel = state.step + 1;

        if (action.step !== undefined)
            stepLevel = action.step;

        return {
            ...state,
            step: stepLevel
        }
    } else if (incomingAction.type === SET_VALIDATION_STATUS) {
        const action = incomingAction as SetValidationStatusAction;

        const q = state.survey.questions.findIndex( f=> f.questionId === action.questionId);

        if (q === -1 || state.survey.questions[q].isValid === action.validationStatus)
            return state;

        var newState = produce(state, draftSate => {
            if (state.isSubmitted) {
                draftSate.survey.questions[q].isValid = action.validationStatus;
            }
        });

        return newState;

    } else if (incomingAction.type === SET_VALIDATION_STATUS_AND_VALUE) {
        const action = incomingAction as SetValidationStatusAndValueAction;
        
        const q = state.survey.questions.findIndex( f=> f.questionId === action.answerValue.questionId);

        if (q === -1)
            return state;

        var newStateResult = produce(state, draftSate => {
            if (state.survey.questions[q].code === fromConstants.QuestionType_MCQ) {
                if (state.isSubmitted || action.answerValue.isSubmitted) {
                    draftSate.survey.questions[q].isValid = action.answerValue.isValid;
                }

                let answerList = [ ...(state.survey.questions[q].mcqAnswers || [])];

                if (action.answerValue.isChecked === false ) {
                    const ansIndex = answerList.findIndex(f => f.mcqOptionId === action.answerValue.optionId);

                    if (ansIndex >= 0) {
                        draftSate.survey.questions[q].mcqAnswers.splice(ansIndex, 1);
                    }
                } else  if (action.answerValue.isOtherTextBoxChanged) {

                    const mcqOption = state.survey.questions[q].mcqOptions.find(f=> f.isOther === true);

                    if (mcqOption) {
                        const ansIndex = answerList.findIndex(f => f.mcqOptionId === mcqOption.mcqOptionId);

                        if (state.survey.questions[q].maxMcqValues <= 1)
                            draftSate.survey.questions[q].mcqAnswers = [];

                        if (ansIndex < 0) {
                            answerList.push(new McqAnswerModel(mcqOption.mcqOptionId, mcqOption.optionEn, mcqOption.optionFr));
                        } else {
                            answerList[ansIndex].answerEn = action.answerValue.text;
                            answerList[ansIndex].answerFr = action.answerValue.text;
                        }
                        
                        draftSate.survey.questions[q].mcqAnswers = answerList;
                    }
                } else {
                    let mcqOption = state.survey.questions[q].mcqOptions.find(f=> f.mcqOptionId === action.answerValue.optionId);

                    const ansIndex = answerList.findIndex(f => f.mcqOptionId === action.answerValue.optionId);
                    
                    if (ansIndex < 0 && mcqOption) {
                        
                        if (isRadio(state.survey.questions[q].minMcqValues, state.survey.questions[q].maxMcqValues))
                            answerList = [];
                        
                        if (mcqOption.isOther)
                            answerList.push(new McqAnswerModel(mcqOption.mcqOptionId, '',''));
                        else
                            answerList.push(new McqAnswerModel(mcqOption.mcqOptionId, mcqOption.optionEn, mcqOption.optionFr));

                        draftSate.survey.questions[q].mcqAnswers = answerList;
                    }
                }
            } if (state.survey.questions[q].code === fromConstants.QuestionType_MATRIX) {
                if (state.isSubmitted || action.answerValue.isSubmitted) {
                    draftSate.survey.questions[q].isValid = action.answerValue.isValid;
                }

////////////////////

let matrixAnswerList = [ ...(state.survey.questions[q].matrixAnswers || [])];

if (action.answerValue.isChecked === false ) {
    const ansIndex = matrixAnswerList.findIndex(f => f.matrixRowId === action.answerValue.rowId && f.matrixColumnId === action.answerValue.columnId);

    if (ansIndex >= 0) {
        draftSate.survey.questions[q].matrixAnswers.splice(ansIndex, 1);
    }
} else {
    let matrixCol = state.survey.questions[q].matrixColumns.find(f=> f.matrixId === action.answerValue.columnId);
    let matrixRow = state.survey.questions[q].matrixRows.find(f=> f.matrixId === action.answerValue.rowId);

    if (matrixCol && matrixRow) {
        
        matrixAnswerList = matrixAnswerList.filter(f => f.matrixRowId !== action.answerValue.rowId);
    
        matrixAnswerList.push(new MatrixAnswerModel(matrixRow.matrixId, matrixCol.matrixId, 
            matrixRow.textEn + MatrixRowAndColumnText_Seperator + matrixCol.textEn, 
            matrixRow.textFr + MatrixRowAndColumnText_Seperator + matrixCol.textFr));

        draftSate.survey.questions[q].matrixAnswers = matrixAnswerList;
    }
}
///////////////////

            } else {
                if (state.isSubmitted || action.answerValue.isSubmitted) {
                    draftSate.survey.questions[q].isValid = action.answerValue.isValid;
                }

                draftSate.survey.questions[q].value = action.answerValue.text;
            }
        });

        return newStateResult;

    } else if (incomingAction.type === SET_SUBMISSION_FLAG) {
        // const action = incomingAction as SetSubmissionFlagAction;

        return  { ...state, isSubmitted: true };

    } else if (incomingAction.type === SET_LANGUAGE) {
        const action = incomingAction as SetLanguageSurveyAction;

        const step = action.moveNext ? state.step + 1 :state.step;
        return{
            ...state,
            language: action.language,
            step: step,
            survey: changeLangSurvey(state.survey, action.language)
        }
    }

    return state;
};
